import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { getProfileApi, type ProfileUser } from '@/lib/api'
import { BRAND_MINT } from '@/lib/utils'
import { PhoneNumberCard } from '@/components/PhoneNumberCard'
import { TaxInfoCard } from '@/components/TaxInfoCard'
import { MailIcon, Shield, Calendar, BuildingIcon, Tag } from 'lucide-react'

export const Route = createFileRoute('/dashboard/profile')({
  component: () => {
    const [profile, setProfile] = useState<ProfileUser | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
      let cancelled = false

      async function fetchProfile() {
        try {
          setLoading(true)
          setError(null)
          const data = await getProfileApi()
          if (!cancelled) {
            setProfile(data.user)
          }
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to load profile')
          }
        } finally {
          if (!cancelled) {
            setLoading(false)
          }
        }
      }

      fetchProfile()
      return () => { cancelled = true }
    }, [])

    if (loading) {
      return <ProfileSkeleton />
    }

    if (error || !profile) {
      return (
        <div className="mx-auto max-w-2xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
            <p className="text-sm text-destructive">{error || 'Profile not found'}</p>
          </div>
        </div>
      )
    }

    const memberSince = new Date(profile.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })

    return (
      <div className="mx-auto max-w-2xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div>
              <h1 className="text-2xl font-semibold">
                Welcome back, {profile.name}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Your account details, role, and member information at a glance.
              </p>
            </div>
          </div>
        </div>

        {/* Avatar & name card */}
        <div className="mb-8 flex items-center gap-5 rounded-xl border border-border bg-card p-6">
          <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            {profile.profile_photo_url ? (
              <img
                src={profile.profile_photo_url}
                alt={profile.name}
                className="size-16 rounded-full object-cover"
              />
            ) : (
              <span className="text-xl font-semibold">
                {profile.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold">{profile.name}</h2>
            <p className="truncate text-sm text-muted-foreground">{profile.pseudonym}</p>
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              {profile.role}
            </span>
          </div>
        </div>

        {/* Detail fields */}
        <div className="space-y-4 rounded-xl border border-border bg-card p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Account details
          </h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <MailIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="truncate text-sm font-medium">{profile.email}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Tag className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Pseudonym</p>
                <p className="truncate text-sm font-medium">{profile.pseudonym}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Shield className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Role</p>
                <p className="truncate text-sm font-medium capitalize">{profile.role}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <BuildingIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Organization ID</p>
                <p className="truncate text-sm font-medium">{profile.org_id ?? '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 sm:col-span-2">
              <Calendar className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Member since</p>
                <p className="text-sm font-medium">{memberSince}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Self-service phone link/unlink/change (DESIGN_PhoneLogin.md §5.2) */}
        <div className="mt-8">
          <PhoneNumberCard />
        </div>

        {/* Self-service tax information (DESIGN_TaxFilingProvision.md §3.3) */}
        <div className="mt-8">
          <TaxInfoCard />
        </div>
      </div>
    )
  },
})

function ProfileSkeleton() {
  return (
    <div className="mx-auto max-w-2xl animate-pulse">
      <div className="mb-8">
        <div className="h-7 w-24 rounded bg-muted" />
        <div className="mt-2 h-4 w-48 rounded bg-muted" />
      </div>

      <div className="mb-8 flex items-center gap-5 rounded-xl border border-border p-6">
        <div className="size-16 rounded-full bg-muted" />
        <div className="space-y-2">
          <div className="h-5 w-32 rounded bg-muted" />
          <div className="h-4 w-24 rounded bg-muted" />
          <div className="h-5 w-16 rounded-full bg-muted" />
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-border p-6">
        <div className="h-4 w-28 rounded bg-muted" />
        <div className="grid gap-4 sm:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-3 w-16 rounded bg-muted" />
              <div className="h-4 w-36 rounded bg-muted" />
            </div>
          ))}
          <div className="space-y-1.5 sm:col-span-2">
            <div className="h-3 w-20 rounded bg-muted" />
            <div className="h-4 w-48 rounded bg-muted" />
          </div>
        </div>
      </div>
    </div>
  )
}
